import { useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Exoneracion } from '@/shared/api/tipos';
import { notificar } from '@/shared/ui/avisos';
import { ESTADOS } from './estadosDeExoneracion';

export function FichaDeExoneracion({
  exoneracion,
  alCerrar,
}: {
  exoneracion: Exoneracion;
  alCerrar: () => void;
}) {
  const e = exoneracion;
  const numero = String(e.id).padStart(6, '0');
  const estado = ESTADOS[e.estado];
  const [descargando, setDescargando] = useState(false);

  async function descargar() {
    setDescargando(true);
    try {
      const pdf = await api.descargar(`/exoneraciones/${e.id}/ficha.pdf`);
      if (!pdf) {
        notificar.error('LA FICHA YA NO ESTÁ DISPONIBLE');
        return;
      }

      const url = URL.createObjectURL(pdf);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `ficha-exoneracion-${numero}.pdf`;
      enlace.click();
      URL.revokeObjectURL(url);

      notificar.exito(`Ficha ${numero} descargada`);
    } catch (causa) {
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR LA FICHA',
      );
    } finally {
      setDescargando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fondo-del-dialogo absolute inset-0 bg-armazon-900/40 backdrop-blur-sm"
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Ficha de exoneración ${numero}`}
        className="para-imprimir relative flex max-h-[95dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="sin-imprimir flex shrink-0 items-center justify-between gap-4 border-b border-armazon-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void descargar()}
              disabled={descargando}
              title="Descargar la ficha en PDF"
              aria-label="Descargar la ficha en PDF"
              className="grid size-10 place-items-center rounded-lg bg-marca-600 text-white transition-colors hover:bg-marca-700 disabled:opacity-50"
            >
              {descargando ? (
                <Loader2 className="size-[18px] animate-spin" aria-hidden />
              ) : (
                <Download className="size-[18px]" aria-hidden />
              )}
            </button>
            <span className="text-[11px] uppercase text-armazon-400">
              Vista previa. El PDF que se archiva lo compone el servidor
            </span>
          </div>

          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-10 py-8">
          <header className="mb-8 border-b-2 border-armazon-900 pb-4 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-armazon-500">
              EPAGAL EP
            </p>
            <p className="text-[10px] uppercase tracking-wide text-armazon-400">
              Empresa Pública de Aseo y Gestión Ambiental del Cantón Latacunga
            </p>
            <h1 className="mt-4 text-lg font-bold uppercase tracking-wide text-armazon-900">
              Ficha de exoneración
            </h1>
            <p className="importe mt-1 text-sm text-armazon-600">N.º {numero}</p>
          </header>

          <Bloque titulo="Beneficiario">
            <Fila etiqueta="Nombre" valor={e.clienteNombre} />
            <Fila etiqueta="Cédula / RUC" valor={e.clienteIdentificacion} mono />
          </Bloque>

          <Bloque titulo="Alcance">
            <Fila
              etiqueta="Cuenta de servicio"
              valor={e.general ? 'TODAS LAS CUENTAS DEL TITULAR' : (e.numeroCuenta ?? '—')}
              mono={!e.general}
            />
          </Bloque>

          <Bloque titulo="Beneficio concedido">
            <Fila etiqueta="Tipo de exoneración" valor={e.tipoNombre.toUpperCase()} />
            <Fila etiqueta="Descuento sobre la tasa" valor={`${e.porcentaje} %`} mono />

            {e.gradoDiscapacidad != null && (
              <Fila
                etiqueta="Grado de discapacidad del carné"
                valor={`${e.gradoDiscapacidad} % — DATO INFORMATIVO, NO ES EL DESCUENTO`}
              />
            )}
            <Fila etiqueta="Base legal" valor={e.baseLegal ?? 'NO CONSTA'} />
            <Fila etiqueta="Documento de respaldo" valor={e.documentoRespaldo ?? 'NO CONSTA'} />
          </Bloque>

          <Bloque titulo="Vigencia">
            <Fila etiqueta="Rige desde" valor={formatearFecha(e.fechaInicio)} />
            <Fila
              etiqueta="Rige hasta"
              valor={e.fechaFin ? formatearFecha(e.fechaFin) : 'VIGENCIA INDEFINIDA'}
            />
            <Fila etiqueta="Situación" valor={estado.etiqueta.toUpperCase()} />
            {e.motivoRechazo && <Fila etiqueta="Motivo" valor={e.motivoRechazo} />}
          </Bloque>

          <Bloque titulo="Responsables">
            <Fila etiqueta="Registrada por" valor={(e.registradoPor ?? 'NO CONSTA').toUpperCase()} />
            <Fila
              etiqueta="Fecha de registro"
              valor={e.registradoEn ? formatearMomento(e.registradoEn) : 'NO CONSTA'}
            />
            <Fila
              etiqueta={estado.descuenta ? 'Aprobada por' : 'Revisada por'}
              valor={(e.revisadoPor ?? 'PENDIENTE DE REVISIÓN').toUpperCase()}
            />
            <Fila
              etiqueta="Fecha de la resolución"
              valor={e.revisadoEn ? formatearMomento(e.revisadoEn) : 'PENDIENTE DE REVISIÓN'}
            />
          </Bloque>

          {e.observaciones && (
            <Bloque titulo="Observaciones">
              <p className="col-span-2 text-justify text-sm text-armazon-800">{e.observaciones}</p>
            </Bloque>
          )}

          <p className="mt-8 border-t border-armazon-200 pt-3 text-justify text-[10px] leading-relaxed text-armazon-500">
            LA EXONERACIÓN SE APLICA SOBRE LA TASA DE GESTIÓN INTEGRAL DE RESIDUOS SÓLIDOS
            MIENTRAS ESTÉ APROBADA Y DENTRO DE SU VIGENCIA. SU PÉRDIDA O MODIFICACIÓN DEBE
            COMUNICARSE A EPAGAL EP, QUE PODRÁ VERIFICAR EN CUALQUIER MOMENTO QUE SE MANTIENEN
            LAS CONDICIONES QUE LA MOTIVARON.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-10">
            <Firma
              rotulo="Registró"
              nombre={(e.registradoPor ?? '—').toUpperCase()}
              detalle={fechaDelMomento(e.registradoEn)}
            />
            <Firma
              rotulo={estado.descuenta ? 'Aprobó' : 'Revisó'}
              nombre={(e.revisadoPor ?? '—').toUpperCase()}
              detalle={fechaDelMomento(e.revisadoEn)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 border-b border-armazon-200 pb-1 text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
        {titulo}
      </h2>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </section>
  );
}

function Fila({
  etiqueta,
  valor,
  mono = false,
}: {
  etiqueta: string;
  valor: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-armazon-400">{etiqueta}</p>
      <p className={`break-words text-sm text-armazon-900 ${mono ? 'importe font-medium' : ''}`}>
        {valor}
      </p>
    </div>
  );
}

function Firma({
  rotulo,
  nombre,
  detalle,
}: {
  rotulo: string;
  nombre: string;
  detalle: string;
}) {
  return (
    <div className="text-center">
      <div className="mb-1 h-12 border-b border-armazon-400" />
      <p className="text-xs font-medium uppercase text-armazon-800">{nombre}</p>
      <p className="importe text-[11px] text-armazon-600">{detalle}</p>
      <p className="text-[10px] uppercase tracking-wider text-armazon-400">{rotulo}</p>
    </div>
  );
}

function formatearFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  const MESES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  return `${dia} de ${MESES[Number(mes) - 1] ?? mes} de ${anio}`;
}

function fechaDelMomento(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const momento = new Date(iso);
  if (Number.isNaN(momento.getTime())) {
    return iso;
  }
  const local = `${momento.getFullYear()}-${String(momento.getMonth() + 1).padStart(2, '0')}-${String(
    momento.getDate(),
  ).padStart(2, '0')}`;
  return formatearFecha(local);
}

function formatearMomento(iso: string): string {
  const momento = new Date(iso);
  return Number.isNaN(momento.getTime()) ? iso : momento.toLocaleString('es-EC');
}
